import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id?: string
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    align?: 'start' | 'center'
    tone?: 'light' | 'dark'
    className?: string
}

export const Eyebrow: FC<{ children: ReactNode, tone?: 'light' | 'dark', className?: string }> = ({
    children,
    tone = 'light',
    className
}): ReactNode => {
    return (
        <p
            className={cn(
                'inline-flex items-center gap-3 text-sm font-semibold',
                tone === 'dark' ? 'text-gold' : 'text-forest-600',
                className
            )}
        >
            <span aria-hidden='true' className='flex items-center gap-1.5'>
                <span className='h-px w-8 bg-gold/70' />
                <span className='size-1.5 rotate-45 bg-gold' />
            </span>

            {children}
        </p>
    )
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    eyebrow,
    title,
    description,
    align = 'start',
    tone = 'light',
    className
}): ReactNode => {
    return (
        <div
            className={cn(
                'max-w-2xl',
                align === 'center' && 'mx-auto text-center [&>p:first-child]:justify-center',
                className
            )}
        >
            {eyebrow && (
                <Eyebrow tone={tone}>
                    {eyebrow}
                </Eyebrow>
            )}

            <h2
                id={id}
                className={cn(
                    'mt-4 text-[2.15rem] font-bold sm:text-[2.75rem]',
                    tone === 'dark' ? 'text-cream' : 'text-forest-900'
                )}
            >
                {title}
            </h2>

            {description && (
                <p
                    className={cn(
                        'mt-4 text-base leading-8 sm:text-lg sm:leading-9',
                        tone === 'dark' ? 'text-cream/70' : 'text-muted-foreground'
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    )
}
